import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	Locality,
	OcupationClassification,
	SalaryRange,
	SchoolCatalog,
	FederalState,
	SubOcupationClasification,
	TaxRegime,
	CNBVColony,
	EconomicSectorResponse,
	EconomicActivityResponse,
	CobisFederalState,
	CobisLocality,
	CobisColony,
} from 'src/app/models/apis/catalogs';
import {
	CmvHttpEntidadResponse,
	CobisCmvHttpResponse,
} from 'src/app/models/apis/entities';
import { ValueDescription } from 'src/app/models/shared/custom-dictionaries';
import { environment } from 'src/environments/environment';
import { HttpHeadersBuilder } from './utils/http-header-builder';
import { City, Colony, State } from 'src/app/models/catalogs/states.model';
import {
	EconomicActivity,
	EconomicSector,
} from 'src/app/models/catalogs/ecomic-sector.model';
import { capitalizeFirstLetter } from 'src/app/data/utils/strings.utils';
import { Coords } from 'src/app/data/utils/geolocation.utils';
import { Branch } from 'src/app/models/catalogs/branch.model';
import { BranchResponse } from 'src/app/models/apis/branches.model';

@Injectable({
	providedIn: 'root',
})
export class CatalogsService {
	url: string = `${environment.urlBase}/onboarding/`;

	constructor(private httpClient: HttpClient) {}

	getCityWithColoniesByPostalCode(postalCode: string): Observable<Locality> {
		const params = new HttpParams().set('cp', postalCode);
		const body = this.getBody();
		const headers = new HttpHeadersBuilder().withClientId().getHeaders();
		return this.httpClient
			.post<CmvHttpEntidadResponse<FederalState>>(
				`${this.url}catalogo_cp`,
				body,
				{
					headers: headers,
					params: params,
				}
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<FederalState>) => {
					if (
						response.Estado &&
						response.Codigo === '200' &&
						response.Entidad.Localidades?.length === 1
					) {
						return response.Entidad.Localidades[0];
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	private getBody() {
		return {
			usuario: 'CMVAPLICACIONES',
			contrasena: 'B68571606C88ECB993E6B983C27A61',
		};
	}

	getEducationLevels(): Observable<Array<ValueDescription<number>>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<SchoolCatalog>>>(
				`${this.url}schoolCatalog`,
				null,
				{ headers: headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<Array<SchoolCatalog>>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad.map((shoolCatalog: SchoolCatalog) => {
							const educationLevel: ValueDescription<number> = {
								value: shoolCatalog.Id_Escolaridad,
								description: shoolCatalog.Descripcion,
							};
							return educationLevel;
						});
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getOcupations(): Observable<Array<ValueDescription<number>>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<OcupationClassification>>>(
				`${this.url}clasProfesion`,
				null,
				{ headers: headers }
			)
			.pipe(
				map(
					(
						response: CmvHttpEntidadResponse<Array<OcupationClassification>>
					) => {
						if (response.Estado && response.Codigo === '200') {
							return response.Entidad.map(
								(ocupationCatalog: OcupationClassification) => {
									const ocupation: ValueDescription<number> = {
										value: ocupationCatalog.Id_Clasif_Ocupacion,
										description: ocupationCatalog.Descripcion,
									};
									return ocupation;
								}
							);
						}
						throw new Error(response.Mensaje);
					}
				)
			);
	}

	getSubOcupations(
		idOcupation: number
	): Observable<Array<ValueDescription<number>>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams().set('IdClasifOcupacion', idOcupation);

		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<SubOcupationClasification>>>(
				`${this.url}subClasProfesion`,
				null,
				{ headers: headers, params: params }
			)
			.pipe(
				map(
					(
						response: CmvHttpEntidadResponse<Array<SubOcupationClasification>>
					) => {
						if (response.Estado && response.Codigo === '200') {
							return response.Entidad.map(
								(subOcupationCatalog: SubOcupationClasification) => {
									const ocupation: ValueDescription<number> = {
										value: subOcupationCatalog.Id_Subclasif_Ocupacion,
										description: subOcupationCatalog.Descripcion,
									};
									return ocupation;
								}
							);
						}
						throw new Error(response.Mensaje);
					}
				)
			);
	}

	getSalaryRanges(): Observable<Array<ValueDescription<number>>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<SalaryRange>>>(
				`${this.url}salaryRanges`,
				null,
				{ headers: headers }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<Array<SalaryRange>>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad.map((salaryRange: SalaryRange) => {
							const educationLevel: ValueDescription<number> = {
								value: salaryRange.id_rango_salario,
								description: salaryRange.leyenda,
							};
							return educationLevel;
						});
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getTaxRegimes(
		forIndividuals: boolean,
		forCorporations: boolean
	): Observable<Array<ValueDescription<number>>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();

		const params = new HttpParams()
			.set('personaFisica', forIndividuals)
			.set('personaMoral', forCorporations);

		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<TaxRegime>>>(
				`${this.url}getTaxRegimes`,
				null,
				{ headers: headers, params: params }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<Array<TaxRegime>>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad.map((salaryRange: TaxRegime) => {
							const educationLevel: ValueDescription<number> = {
								value: salaryRange.Id_SAT,
								description: `${salaryRange.clave_regimen_fiscal} - ${salaryRange.Descripcion}`,
							};
							return educationLevel;
						});
					}
					throw new Error(response.Mensaje);
				})
			);
	}

	getStates(): Observable<Array<State>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams().set('id_pais', 484);
		return this.httpClient
			.get<CobisCmvHttpResponse<Array<CobisFederalState>>>(
				`${this.url}provincias`,
				{ headers, params }
			)
			.pipe(
				map((response: CobisCmvHttpResponse<Array<CobisFederalState>>) => {
					const { estado, codigo, modelo } = response;
					if (estado && codigo === 200) {
						return modelo.map(federalState => {
							const state: State = {
								stateId: federalState.codigo_estado,
								description: federalState.descripcion,
								key: federalState.clave_estado + '',
							};
							return state;
						});
					}

					throw new Error(response.mensaje);
				})
			);
	}

	getCities(idEntidadFederativa): Observable<Array<City>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams().set(
			'id_entidad_federativa',
			idEntidadFederativa
		);
		return this.httpClient
			.get<CobisCmvHttpResponse<Array<CobisLocality>>>(`${this.url}ciudades`, {
				headers,
				params,
			})
			.pipe(
				map((response: CobisCmvHttpResponse<Array<CobisLocality>>) => {
					const { estado, codigo, modelo } = response;
					if (estado && codigo === 200) {
						return modelo.map(locality => {
							const city: City = {
								cityId: locality.codigo_ciudad,
								name: locality.nombre_localidad,
								key: locality.codigo_ciudad,
								stateId: locality.codigo_estado,
							};
							return city;
						});
					}

					throw new Error(response.mensaje);
				})
			);
	}

	getColonies(cityId: number): Observable<Array<Colony>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();
		const params = new HttpParams().set('id_localidad', cityId);
		return this.httpClient
			.get<CobisCmvHttpResponse<Array<CobisColony>>>(`${this.url}parroquias`, {
				headers,
				params,
			})
			.pipe(
				map((response: CobisCmvHttpResponse<Array<CobisColony>>) => {
					const { estado, codigo, modelo } = response;
					if (estado && codigo === 200) {
						return modelo
							.filter(function (colonia) {
								return (
									colonia.nombre_colonia !== null &&
									colonia.nombre_colonia !== undefined &&
									colonia.nombre_colonia !== ''
								);
							})
							.map(cnbvColony => {
								const colony: Colony = {
									colonyId: cnbvColony.codigo_colonia,
									name: cnbvColony.nombre_colonia,
									postalCode: cnbvColony.codigo_postal,
									type: cnbvColony.tipo_colonia,
									cityId: cnbvColony.codigo_ciudad,
								};
								return colony;
							});
					}

					throw new Error(response.mensaje);
				})
			);
	}

	getEcomicSectors(): Observable<Array<EconomicSector>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<EconomicSectorResponse>>>(
				`${this.url}getEconomicSectors`,
				null,
				{ headers }
			)
			.pipe(
				map(
					(response: CmvHttpEntidadResponse<Array<EconomicSectorResponse>>) => {
						const { Estado, Codigo, Entidad } = response;
						if (Estado && Codigo === '200') {
							return Entidad.map(economicSectorRsponse => {
								const economicSector: EconomicSector = {
									economicSectorId: economicSectorRsponse.id_sector_economico,
									description: capitalizeFirstLetter(
										economicSectorRsponse.descripcion
									),
									isActive: economicSectorRsponse.activo,
								};
								return economicSector;
							});
						}

						throw new Error(response.Mensaje);
					}
				)
			);
	}

	getEconomicActivities(
		economicSectorId: number
	): Observable<Array<EconomicActivity>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams().set(
			'id_sector_economico',
			economicSectorId
		);

		return this.httpClient
			.post<CmvHttpEntidadResponse<Array<EconomicActivityResponse>>>(
				`${this.url}getEconomicActivity`,
				null,
				{ headers, params }
			)
			.pipe(
				map(
					(
						response: CmvHttpEntidadResponse<Array<EconomicActivityResponse>>
					) => {
						const { Estado, Codigo, Entidad } = response;
						if (Estado && Codigo === '200') {
							return Entidad.map(cnbvColony => {
								const colony: EconomicActivity = {
									economicActivityId: cnbvColony.id_actividad_economica,
									economicSectorId: cnbvColony.id_sector_economico,
									description: cnbvColony.descripcion,
									isActive: cnbvColony.activo,
								};
								return colony;
							});
						}

						throw new Error(response.Mensaje);
					}
				)
			);
	}

	getBranchesByCoords(
		coords: Coords,
		applicantId: number
	): Observable<Array<Branch>> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams()
			.set('latitud', coords.latitude)
			.set('longitud', coords.longitude)
			.set('num_sucursales', 5)
			.set('num_socio', applicantId);

		return this.httpClient
			.get<Array<BranchResponse>>(`${this.url}getNearbyOffices`, {
				headers,
				params,
			})
			.pipe(
				map((response: Array<BranchResponse>) => {
					return response.map(branchResponse => ({
						branchId: branchResponse.Id_de_Sucursal,
						description: branchResponse.Descripcion,
						street: branchResponse.Calle,
						exteriorNumber: branchResponse.Numero_exterior,
						colonyId: branchResponse.Id_Colonia_CNBV,
						colony: branchResponse.Nombre_Colonia,
						city: branchResponse.Ciudad,
						latitude: branchResponse.LATITUD,
						longitude: branchResponse.LONGITUD,
						distanceKm: branchResponse.distancia_km,
					}));
				})
			);
	}
}
